import { useEffect, useState } from "react";
import { Plus, Trash, X, Bell, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useCurrency } from "../../lib/useCurrency";
import { Th, Td, StatusPill } from "./TrainerSchedule";

export default function TrainerInvoices() {
  const { symbol } = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
      setInvoices(i.data);
      setClients(c.data);
    } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    try { await api.patch(`/invoices/${id}/mark-paid`); toast.success("Marked as made"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete invoice?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendNotice = async (id) => {
    try {
      await api.post("/notifications/send", { invoice_id: id, channels: ["email"] });
      toast.success("Notification sent");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const totalUnpaid = invoices.filter(i => i.status === "unpaid").reduce((s, i) => s + i.amount, 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-8" data-testid="trainer-invoices">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Billing ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Invoices.</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="open-invoice-form">
          <Plus size={16} weight="bold" /> New invoice
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 border border-white/10">
        <Mini label="Invoices due" value={`${symbol}${totalUnpaid.toFixed(2)}`} accent />
        <Mini label="Invoices paid" value={`${symbol}${totalPaid.toFixed(2)}`} />
        <Mini label="Count" value={invoices.length} />
      </div>

      <div className="card-flat overflow-x-auto">
        {invoices.length === 0 ? (
          <div className="p-16 text-center text-zinc-500 tracker">No invoices yet</div>
        ) : (
          <table className="w-full" data-testid="invoices-table">
            <thead>
              <tr className="border-b border-white/10">
                <Th>Created</Th><Th>Client</Th><Th>Description</Th><Th>Amount</Th><Th>Status</Th><Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-white/5 hover:bg-[#1A1A1A]" data-testid={`invoice-row-${inv.id}`}>
                  <Td><span className="tracker">{new Date(inv.created_at).toLocaleDateString()}</span></Td>
                  <Td><div className="font-semibold">{inv.client?.name || "—"}</div><div className="text-xs text-zinc-500">{inv.client?.email}</div></Td>
                  <Td><span className="text-sm">{inv.description}</span></Td>
                  <Td>
                    <span className="font-mono money">{inv.currency?.toUpperCase()} {inv.amount.toFixed(2)}</span>
                    {inv.vat_amount ? (
                      <div className="tracker text-[9px] text-zinc-500 mt-0.5" data-testid={`invoice-vat-${inv.id}`}>
                        incl. {inv.vat_percent}% VAT · {inv.currency?.toUpperCase()} {Number(inv.vat_amount).toFixed(2)}
                        {inv.vat_number ? <span className="ml-1 font-mono">({inv.vat_number})</span> : null}
                      </div>
                    ) : null}
                  </Td>
                  <Td><StatusPill status={inv.status} /></Td>
                  <Td align="right">
                    <div className="flex items-center gap-2 justify-end">
                      {inv.status === "unpaid" && (
                        <>
                          <button onClick={() => sendNotice(inv.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`notify-invoice-${inv.id}`} title="Notify client">
                            <Bell size={14} weight="bold" />
                          </button>
                          <button onClick={() => markPaid(inv.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`mark-paid-${inv.id}`} title="Mark as made">
                            <CheckCircle size={14} weight="bold" />
                          </button>
                        </>
                      )}
                      <button onClick={() => remove(inv.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-invoice-${inv.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && <InvoiceForm onClose={() => setShowForm(false)} onCreated={load} clients={clients} />}
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className={`p-6 border-r border-b border-white/10 ${accent ? "bg-[#1A0F0E]" : ""}`}>
      <div className="tracker mb-3">{label}</div>
      <div className={`font-display text-3xl money ${accent ? "text-[#FF6B61]" : ""}`}>{value}</div>
    </div>
  );
}

function InvoiceForm({ onClose, onCreated, clients }) {
  const [form, setForm] = useState({ client_id: clients[0]?.id || "", amount: 0, currency: "usd", description: "Monthly training package" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Add a client first");
    setLoading(true);
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Invoice created — pot will top up when paid");
      onCreated(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const presets = [
    { label: "1mo · 4 sessions", amount: 320, desc: "Monthly package — 4 sessions" },
    { label: "1mo · 8 sessions", amount: 600, desc: "Monthly package — 8 sessions" },
    { label: "1mo · 12 sessions", amount: 840, desc: "Monthly package — 12 sessions" },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="invoice-form-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ New invoice ]</div>
            <h2 className="font-display text-3xl">Invoice.</h2>
            <p className="text-zinc-500 text-xs mt-1">When the invoice is paid, the client's pot is auto-credited.</p>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Client</label>
            <select required value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 border" data-testid="invoice-client">
              {clients.length === 0 && <option value="">Add a client first</option>}
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — ${Number(c.balance || 0).toFixed(2)} in pot</option>)}
            </select>
          </div>

          <div>
            <label className="tracker block mb-2">Quick package</label>
            <div className="grid grid-cols-3 gap-2" data-testid="invoice-presets">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, amount: p.amount, description: p.desc }))}
                  className={`p-3 border text-left ${Number(form.amount) === p.amount ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/40"}`}
                >
                  <div className="font-display text-base leading-none mb-1">{p.label}</div>
                  <div className="tracker text-zinc-500">${p.amount}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="tracker block mb-2">Description</label>
            <input value={form.description} onChange={set("description")} className="w-full px-4 py-3 border" data-testid="invoice-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="tracker block mb-2">Amount</label>
              <input required type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} className="w-full px-4 py-3 border" data-testid="invoice-amount" />
            </div>
            <div>
              <label className="tracker block mb-2">Currency</label>
              <select value={form.currency} onChange={set("currency")} className="w-full px-4 py-3 border" data-testid="invoice-currency">
                <option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="invoice-submit">
            {loading ? "Creating…" : "Create invoice"}
          </button>
        </form>
      </div>
    </div>
  );
}
